
import React, { useState, useEffect } from 'react';
import { Calendar, CalendarCheck, Clock, Info, GraduationCap, MapPin, Lightbulb, TrendingUp } from 'lucide-react';
import { GoalMode, AudienceType, Environment, AcademicEvent } from '../types';
import EmptyState from '../components/EmptyState';

interface PlannerProps {
  goalMode: GoalMode;
  audienceMode: AudienceType;
  activeEnv: Environment | null;
}

const Planner: React.FC<PlannerProps> = ({ goalMode, audienceMode, activeEnv }) => {
  const [seoSuggestions, setSeoSuggestions] = useState<any[]>([]);

  // Fetch SEO content suggestions
  useEffect(() => {
    if (!activeEnv || activeEnv.isSample) return;

    const fetchSEOSuggestions = async () => {
      try {
        const response = await fetch('/api/seo/research', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'keywords',
            topic: 'laser energetics'
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Filter for high-value content opportunities
          const highValue = (data.data || []).filter((k: any) =>
            k.difficulty < 50 && k.searchVolume > 500
          ).slice(0, 5);
          setSeoSuggestions(highValue);
        }
      } catch (error) {
        console.error('Failed to fetch SEO suggestions:', error);
      }
    };

    fetchSEOSuggestions();
  }, [activeEnv?.id, activeEnv?.isSample]);

  if (!activeEnv) return <EmptyState />;

  const academicCalendar: AcademicEvent[] = [
    { id: '1', title: 'Fall PhD Application Deadline', date: '2024-12-15', type: 'deadline', impact: 'high' },
    { id: '2', title: 'American Physical Society (APS-DPP) Conference', date: '2024-10-21', type: 'conference', impact: 'high' },
    { id: '3', title: 'Campus Lab Open House', date: '2024-11-10', type: 'event', impact: 'medium' }
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900">Campaign Planner</h2>
          <p className="text-slate-500 mt-1 text-sm">Strategic scheduling aligned with the academic and research cycles.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">
           <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
             <div className="flex items-center justify-between mb-8">
               <h3 className="font-black text-xl text-slate-900 flex items-center gap-2">
                 <Calendar size={22} className="text-[#003B71]" />
                 Content Roadmap
               </h3>
               <div className="flex gap-2">
                 <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-3 py-1 rounded-full uppercase tracking-widest">Week 46 (Current)</span>
               </div>
             </div>
             
             <div className="space-y-4">
               {[18, 19, 20, 21, 22].map(day => (
                 <div key={day} className="flex gap-6 p-4 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                    <div className="w-16 flex flex-col items-center justify-center border-r border-slate-100 pr-6">
                      <span className="text-xs font-black text-slate-400 uppercase">Nov</span>
                      <span className="text-2xl font-black text-slate-900">{day}</span>
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                       <div>
                         <p className="text-xs font-black text-[#003B71] uppercase tracking-widest mb-1">LinkedIn / X</p>
                         <h4 className="font-bold text-slate-800">Laser Plasma Interactions (LPI) Research Highlights</h4>
                       </div>
                       <div className="text-right">
                         <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Decision Stage</span>
                         <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-[9px] font-bold">Ready for Review</span>
                       </div>
                    </div>
                 </div>
               ))}
             </div>
           </section>
        </div>

        <div className="space-y-6">
          <section className="bg-[#0B2341] text-white rounded-3xl p-8 shadow-2xl relative overflow-hidden">
             <div className="relative z-10">
               <h3 className="font-black text-lg mb-6 flex items-center gap-2">
                 <CalendarCheck size={18} className="text-[#FFCD00]" />
                 Academic Milestones
               </h3>
               <div className="space-y-6">
                 {academicCalendar.map(event => (
                   <div key={event.id} className="border-l-2 border-[#FFCD00] pl-4">
                      <p className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1">{event.date} • {event.type}</p>
                      <h4 className="text-sm font-bold leading-snug">{event.title}</h4>
                      {event.impact === 'high' && (
                        <div className="mt-2 text-[9px] font-black bg-red-500/20 text-red-200 px-2 py-0.5 rounded inline-block uppercase">Critical Alignment</div>
                      )}
                   </div>
                 ))}
               </div>
             </div>
             <div className="absolute top-0 right-0 p-4 opacity-5">
               <Clock size={120} />
             </div>
          </section>

          {/* SEO-Driven Content Suggestions */}
          {seoSuggestions.length > 0 && (
            <section className="bg-gradient-to-br from-[#FFCD00] to-[#FFE066] rounded-3xl p-6 shadow-lg">
              <h3 className="font-black text-sm mb-4 flex items-center gap-2 text-[#003B71]">
                <Lightbulb size={16} />
                SEO Content Ideas
              </h3>
              <div className="space-y-3">
                {seoSuggestions.map((suggestion, idx) => (
                  <div key={idx} className="bg-white/90 backdrop-blur rounded-xl p-3 border border-[#003B71]/10">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-xs font-bold text-[#003B71] leading-tight flex-1">{suggestion.keyword}</h4>
                      <span className="text-[9px] font-black text-green-700 bg-green-100 px-2 py-0.5 rounded ml-2">
                        {suggestion.searchVolume?.toLocaleString()}/mo
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-600 leading-relaxed">{suggestion.recommendation}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[8px] font-bold text-slate-400 uppercase">{suggestion.intent}</span>
                      <span className="text-[8px] font-bold text-slate-400">•</span>
                      <span className="text-[8px] font-bold text-orange-600">Difficulty: {suggestion.difficulty}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
             <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
               <Info size={14} /> AI Planner Logic
             </h4>
             <p className="text-xs text-slate-500 leading-relaxed font-medium">
               The planner is prioritizing <strong>Consideration</strong> assets this week due to the approaching {academicCalendar[0].title}. Content suggestions are based on SEO keyword research and search volume data.
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Planner;
